import { Checkbox, Collapse, FormControl, MenuItem, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { Stack } from '@mui/system';
import LabelledSelect from '../LabelledSelect';
import { setDeviceOwner, setDeviceOwnerType } from '../../store/upload/uploadSlice';

const DeviceOwnerSelect: React.FC<{ forceDisabled: boolean }> = ({ forceDisabled }) => {
	const dispatch = useAppDispatch();

	const [ownerName, setOwnerName] = useState<string>('');
	const [deviceOwnerUnknown, setDeviceOwnerUnknown] = useState<boolean>(false);

	const [ownerType, setOwnerType] = useState<string>('');
	const [customType, setCustomType] = useState<string>('');

	useEffect(() => {
		if (deviceOwnerUnknown) {
			dispatch(setDeviceOwner('DEVICE_OWNER_UNKNOWN'));
		} else {
			dispatch(setDeviceOwner(ownerName));
		}
	}, [ownerName, deviceOwnerUnknown]);

	useEffect(() => {
		if (ownerType === 'Other') {
			dispatch(setDeviceOwnerType(customType));
		} else {
			dispatch(setDeviceOwnerType(ownerType));
		}
	}, [ownerType, customType]);

	return (
		<Stack gap={1}>
			<FormControl fullWidth>
				<TextField
					variant="outlined"
					value={deviceOwnerUnknown ? 'Unknown' : ownerName}
					onChange={(e) => setOwnerName(e.target.value)}
					helperText={`Name of the person that owns/owned the device. Select the checkbox if the name is unknown. Must be specified before file upload is allowed`}
					disabled={forceDisabled || deviceOwnerUnknown}
					required
					data-testid={`device-owner-select-textfield`}
					label={`${ownerType === 'Other' ? customType : ownerType} Name`}
					InputProps={{
						endAdornment: (
							<Tooltip title="Device Owner Unknown" role="checkbox" aria-checked={deviceOwnerUnknown}>
								<Checkbox
									disabled={forceDisabled}
									onChange={(_, checked) => {
										setDeviceOwnerUnknown(checked);
									}}
									size="large"
									inputProps={{
										'aria-label': 'Device Owner Unknown',
									}}
									data-testid="device-owner-unknown-checkbox"
								/>
							</Tooltip>
						),
					}}
				/>
			</FormControl>
			<FormControl fullWidth sx={{ my: 1 }}>
				<LabelledSelect
					selectLabel="Type"
					testId="device-owner-type-select"
					value={ownerType}
					label="Type"
					onChange={(e) => setOwnerType(e.target.value)}
					disabled={forceDisabled}
					required={!deviceOwnerUnknown}
				>
					<MenuItem value={'Suspect'} data-testid="device-owner-suspect-option">
						Suspect
					</MenuItem>
					<MenuItem value={'Witness'}>Witness</MenuItem>
					<MenuItem value={'Victim'}>Victim</MenuItem>
					<MenuItem value={'Other'} data-testid="device-owner-other-option">
						Other (please specify)
					</MenuItem>
				</LabelledSelect>
			</FormControl>
			<Collapse orientation="vertical" in={ownerType === 'Other'} sx={{ width: '100%' }}>
				<FormControl fullWidth>
					<TextField
						variant="outlined"
						value={customType}
						onChange={(e) => setCustomType(e.target.value)}
						disabled={forceDisabled}
						data-testid={`device-owner-custom-type-textfield`}
						label="Specify other type"
					/>
				</FormControl>
			</Collapse>
		</Stack>
	);
};

export default DeviceOwnerSelect;
